import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';

/* ============================================================
   VALUES
   ============================================================ */
const values = [
  {
    title: 'Precision',
    desc: 'Every engagement is executed with uncompromising rigor and the highest standards of technical accuracy.',
  },
  {
    title: 'Integrity',
    desc: 'Unwavering commitment to ethical practice, transparency, and regulatory compliance.',
  },
  {
    title: 'Partnership',
    desc: 'We operate as a strategic extension of your executive suite — unconditionally aligned with your objectives and accountable for measurable outcomes.',
  },
  {
    title: 'Innovation',
    desc: 'Modern tools and forward-thinking strategies that deliver efficient, scalable solutions.',
  },
];

/* ============================================================
   WHY US REASONS
   ============================================================ */
const reasons = [
  'Over two decades of hands-on experience in corporate finance, tax, and advisory',
  'Tailored solutions designed around your specific business objectives',
  'Comprehensive service portfolio — tax, accounting, audit, forensic, and consulting',
  'Technology-driven processes that maximize accuracy and efficiency',
  'Proactive, responsive communication with dedicated service teams',
  'Multi-state and multi-jurisdictional expertise across all 50 states',
];

function WhyUs() {
  return (
    <PageTransition>
      {/* ── Hero Section ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--clr-navy) 0%, var(--clr-navy-deep) 100%)',
        padding: '80px 24px 64px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700, color: 'var(--clr-white)',
          marginBottom: 16, lineHeight: 1.15,
        }}>About by HW Consulting</h1>
        <p style={{
          fontSize: 'clamp(1rem, 2vw, 1.12rem)',
          color: 'rgba(255,255,255,0.75)',
          maxWidth: 550, margin: '0 auto', lineHeight: 1.6,
        }}>Your Trusted Partner for Comprehensive Financial Solutions</p>
      </div>

      {/* ── Company Overview ── */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        style={{maxWidth:1200, margin:'0 auto', padding:'100px 32px 80px'}}
      >
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'flex-start'}}>
          <div style={{flex: '1 1 400px'}}>
            <h2 style={{
              fontFamily: "var(--font-heading)",
              fontSize: 'clamp(2rem, 3vw, 2.8rem)',
              fontWeight: 700, color: 'var(--clr-navy)', lineHeight: 1.2, marginBottom: '24px'
            }}>
              Institutional Depth. <span style={{color: 'var(--clr-accent-gold)'}}>Personalized Partnership.</span>
            </h2>
            <div style={{width: 60, height: 3, background: 'var(--clr-accent-gold)', marginBottom: 32}}></div>
            <p style={{fontSize:'1.1rem', color:'var(--clr-gray)', lineHeight:1.6, fontWeight: 500}}>
              We bring the depth and breadth of capabilities typically found at large national firms, combined with the personalized attention of a dedicated partner.
            </p>
          </div>
          <div style={{flex: '1 1 500px'}}>
            <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8, marginBottom:24}}>
              At by HW Consulting, we engineer advanced tax strategies designed for scalable growth and strict regulatory compliance. Our firm delivers full-suite tax planning, outsourced accounting, and advisory solutions, backed by a consortium of elite strategic finance professionals.
            </p>
            <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8, marginBottom:24}}>
              From multi-state tax compliance and proactive tax planning to forensic investigations and strategic CFO services, we deliver end-to-end tax and financial leadership that drives measurable results.
            </p>
            <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8}}>
              We serve clients across all 50 states, with particular expertise in real estate, healthcare, technology, non-profit, and professional services sectors.
            </p>
          </div>
        </div>
      </motion.div>

      {/* ── Our Team ── */}
      <div style={{background:'var(--clr-offwhite)', padding:'100px 24px', borderTop:'1px solid var(--clr-border)', borderBottom:'1px solid var(--clr-border)'}}>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          style={{maxWidth:1200, margin:'0 auto'}}
        >
          <div style={{display: 'flex', flexWrap: 'wrap-reverse', gap: '64px', alignItems: 'center'}}>
            <div style={{flex: '1 1 400px'}}>
              <h2 style={{
                fontFamily: "var(--font-heading)",
                fontSize: 'clamp(2rem, 3.5vw, 2.8rem)',
                fontWeight: 700, color: 'var(--clr-navy)', marginBottom: 8,
              }}>Our Team</h2>
              <div style={{width:60, height:3, background:'var(--clr-accent-gold)', margin:'20px 0 32px 0'}}></div>
              <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8, marginBottom: 24}}>
                We bring together a diverse consortium of top-tier financial analysts and strategic advisory professionals. 
              </p>
              <p style={{fontSize:'1.05rem', color:'var(--clr-text-light)', lineHeight:1.8}}>
                Our culture is rooted in collaboration, precision, and a relentless commitment to our clients' success. By operating as a unified front, we deliver institutional-grade financial architectures with the personalized touch of a dedicated partner.
              </p>
            </div>
            <div style={{flex: '1 1 500px'}}>
              <img 
                src="/group_photo.webp" 
                alt="Our Team at by HW Consulting" 
                loading="lazy" 
                style={{
                  width:'100%', 
                  borderRadius:'24px', 
                  boxShadow:'0 20px 40px rgba(0,0,0,0.1)', 
                  display:'block',
                  objectFit: 'cover',
                  maxHeight: '500px'
                }} 
              />
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Our Approach ── */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
        className="byhw-process-section"
      >
        <div style={{textAlign:'center'}}>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--clr-navy)', marginBottom: 8,
          }}>Our Approach</h2>
          <div style={{width:50, height:3, background:'var(--clr-accent-gold)', margin:'16px auto 0'}}></div>
          <p style={{fontSize:'1rem', color:'var(--clr-text-light)', marginTop:14, maxWidth:500, marginLeft:'auto', marginRight:'auto'}}>
            A disciplined methodology that delivers measurable results
          </p>
        </div>
        <div className="byhw-process-grid">
          <div className="byhw-process-step">
            <div className="byhw-process-num">01</div>
            <h3>Assess</h3>
            <p>Thorough review of your financial position, compliance standing, and operational requirements.</p>
          </div>
          <div className="byhw-process-step">
            <div className="byhw-process-num">02</div>
            <h3>Strategize</h3>
            <p>Tailored engagement plan with clear deliverables, timelines, and accountability.</p>
          </div>
          <div className="byhw-process-step">
            <div className="byhw-process-num">03</div>
            <h3>Execute</h3>
            <p>Precision delivery with ongoing advisory, reporting, and proactive communication.</p>
          </div>
        </div>
      </motion.div>

      {/* ── Why Choose Us ── */}
      <div style={{padding:'72px 24px', background:'var(--clr-white)'}}>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          style={{maxWidth:800, margin:'0 auto'}}
        >
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--clr-navy)', textAlign: 'center', marginBottom: 8,
          }}>Why Choose by HW Consulting</h2>
          <div style={{width:50, height:3, background:'var(--clr-accent-gold)', margin:'16px auto 40px'}}></div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16}}>
            {reasons.map((item, i) => (
              <div key={i} style={{
                display:'flex', alignItems:'flex-start', gap:14, padding:'16px 18px',
                background:'var(--clr-offwhite)', borderRadius:'var(--radius-md)', border:'1px solid var(--clr-border)',
              }}>
                <div style={{
                  width:22, height:22, borderRadius:'50%', background:'var(--clr-navy)', color:'var(--clr-white)',
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1,
                }}>
                  <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"/>
                  </svg>
                </div>
                <span style={{fontSize:'0.95rem', color:'var(--clr-text)', lineHeight:1.55, fontWeight:500}}>{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ── Core Values ── */}
      <div style={{background:'var(--clr-offwhite)', padding:'72px 24px', borderTop:'1px solid var(--clr-border)', borderBottom:'1px solid var(--clr-border)'}}>
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          style={{maxWidth:1000, margin:'0 auto', textAlign:'center'}}
        >
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--clr-navy)', marginBottom: 8,
          }}>Our Core Values</h2>
          <div style={{width:50, height:3, background:'var(--clr-accent-gold)', margin:'16px auto 44px'}}></div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:20}}>
            {values.map((item, i) => (
              <div key={i} style={{
                textAlign:'center', padding:'36px 20px', background:'var(--clr-white)',
                borderRadius:'var(--radius-md)', border:'1px solid var(--clr-border)', transition:'all 0.3s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
              >
                <div style={{
                  width:44, height:44, borderRadius:'50%', background:'var(--clr-navy)', color:'var(--clr-white)',
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  fontSize:'0.95rem', fontWeight:700, marginBottom:18,
                  fontFamily:'var(--font-heading)',
                }}>{String(i+1).padStart(2,'0')}</div>
                <h3 style={{
                  fontFamily:'var(--font-heading)',
                  fontSize:'1.1rem', fontWeight:700, color:'var(--clr-navy)', marginBottom:10,
                }}>{item.title}</h3>
                <p style={{fontSize:'0.88rem', color:'var(--clr-text-light)', lineHeight:1.6}}>{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ── Industries ── */}
      <div style={{padding:'72px 24px', background:'var(--clr-white)'}}>
        <div style={{textAlign:'center', maxWidth:900, margin:'0 auto'}}>
          <h2 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--clr-navy)', marginBottom: 8,
          }}>Industries We Serve</h2>
          <div style={{width:50, height:3, background:'var(--clr-accent-gold)', margin:'16px auto 0'}}></div>
          <p style={{fontSize:'1rem', color:'var(--clr-text-light)', marginTop:14, maxWidth:500, marginLeft:'auto', marginRight:'auto'}}>
            Deep sector expertise for better business outcomes
          </p>
          <div className="premium-text-list" style={{ marginTop: 48 }}>
            {[
              'Real Estate', 'Healthcare', 'Technology',
              'Non-Profit', 'Retail & E-Commerce', 'Professional Services',
              'Construction', 'Manufacturing', 'Financial Services',
            ].map((ind, i) => (
              <div key={i} className="premium-text-item" style={{cursor:'default'}}>
                <span className="premium-bullet"></span>
                {ind}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <section style={{
        background:'linear-gradient(135deg, var(--clr-navy) 0%, var(--clr-navy-deep) 100%)',
        padding:'56px 24px', textAlign:'center',
      }}>
        <p style={{
          fontSize:'clamp(1.1rem, 2vw, 1.3rem)', color:'rgba(255,255,255,0.75)',
          maxWidth:600, margin:'0 auto 28px', lineHeight:1.7,
        }}>
          Ready to elevate your financial operations? Let us put our expertise to work for your business.
        </p>
        <Link to="/contact" className="byhw-cta" style={{padding:'13px 36px', fontSize:'0.95rem'}}>
          Schedule a Consultation
        </Link>
      </section>
    </PageTransition>
  );
}

export default WhyUs;